import { useEffect, useRef, useState } from "react";
import { api } from "./api/api";
import { Pdf } from "./core/pdf/pdf";
import { PdfDefaults, report_def } from "./core/pdf/types";
import { getQueryStringValue, url } from "./core/route/Route";
import { expectArray, first } from "./core/utils/array";
import { account_wf, event } from "./types";

import JSZip, { file } from "jszip";
import { Spinner } from "./core/ui/Spinner";
import { useRefresh } from "./core/hooks/useRefresh";
import { Icon } from "./core/ui/icons";
import { toBool } from "rant-utils";


export async function createEventPdf(eventID: string) {

	// get event details
	const event: event = first(expectArray(await api.event_get({ id: eventID, })));

	// get report
	let def;

	let title = "Event";
	let message = "No information available";

	def = first(expectArray(await api.report_def_get({
		id: "event-details",
		dependencies: true,
	}))) as report_def;

	if (event.type) {
		if (event.type === "asic-fail") {
			title = "ASIC Registration - Unsuccessful";
			message = event.data.errors;

		} else if (event.type === "asic-ra56") {
			title = "ASIC Advice";
			message = event.data.advice;

		}
	}

	return await createPdf(
		def, 
		{
			event,
			title,
			message,
		}
	);

}

async function createReportPdf(defID: string, formID?: string) {

	const def = first(expectArray(await api.report_def_get({
		//id: "parent-test",
		//id: "e673c9b2-9aa3-43b4-91b8-ecace7793654",
		//id: "company-pdf",
		id: defID,
		dependencies: true,
	}))) as report_def;

	if (!def || !def.data) {
		return undefined;
	}

	let data;
	if (!formID) {
		data = await api.account_form_latest({});
	} else {
		data = await api.account_form_with_def({ id: formID });
	}

	// const data = await api.account_form_get({
	// 	//id: "89901482-3946-4f4c-93f5-b5e84891f16a",
	// 	id: "979987ab-f065-4350-a9fe-4d8eabff4f4d",
	// });

	// def.data.template = {
	// 	blob: "/208_20140701.pdf",
	// 	title: "",
	// };


	return await createPdf(
		def, 
		{
			data,
		}
	);
}

async function createPdf(def: report_def, scriptScope: any) {

	const defaults = def.data.defaults ? def.data.defaults : PdfDefaults;

	if (defaults.lineHeight) defaults.lineHeight = Number(defaults.lineHeight);
	const embeddedFontNames = [defaults.fontName];

	const showFields = toBool(getQueryStringValue("showFields"));

	const pdf = await Pdf({
		scriptScope,
		templateBlob: def.data.template ? def.data.template.blob : undefined,

		defaults,

		embeddedFontNames,

		showFields,

		subReports: def.subReports,
	});

	await pdf.preFetchImages(def.data.sections);

	await pdf.compose(def);

	const bytes = await pdf.save();
	return {
		src: scriptScope,
		bytes,
	}
}

export function GenerateWorkflowPdf(props: {
	defID?: string, 
	formID?: string,
}) {

	const pdfUrl = useRef("");

	useEffect(() => {
		function onUnload() {
			if (pdfUrl.current) {
				URL.revokeObjectURL(pdfUrl.current);
				pdfUrl.current = "";
			}
		}

		window.addEventListener("unload", onUnload);

		return function() {
			window.removeEventListener("unload", onUnload);
			onUnload();
		};
	}, []);

	const [title, setTitle] = useState("");
	
	useEffect(() => {
		createReportPdf(props.defID!, props.formID).then((pdf: any) => {

			if (!pdf) {
				setTitle("Error: failed to load report definition");
				return;
			}

			// Assuming pdfDoc is your dynamically generated PDF content (e.g., ArrayBuffer or Uint8Array)
			// Convert the PDF content to a blob
			const pdfBlob = new Blob([pdf.bytes as any], { type: 'application/pdf', });

			pdfUrl.current = URL.createObjectURL(pdfBlob);

			let title = pdf.src.data.title;
			const fn = url.get("fn");
			if (fn) {
				title = decodeURIComponent(fn);
			}

			setTitle(title);

			// // const w = window.open(pdfUrl);
			// // if (w) {
			// // 	w!.document.title = "CustomFilename.pdf"
			// // }

			// const method: string = "embed"; //"iframe";

			// if (method === "iframe") {
			// 	// Create an iframe element to display the PDF
			// 	const iframe = document.createElement('iframe');

			// 	iframe.addEventListener("unload", () => {
			// 		/// ****************************************************
			// 		// IMPORTANT - also need to factor in clean up here
			// 		// should be using URL.revokeObjectURL() to free mem
			// 		alert("a")
			// 		URL.revokeObjectURL(pdfUrl);
			// 	});

			// 	iframe.src = pdfUrl;
			// 	iframe.style.width = '100vw';
			// 	iframe.style.height = '100vh';

			// 	document.body.appendChild(iframe);

			// } else if (method === "embed") {

			// 	// const div = document.createElement("div");
			// 	// document.body.appendChild()


			// 	// const o = document.createElement("embed");
			// 	// o.src = pdfUrl;
			// 	// o.type = "application/pdf";
			// 	// document.body.appendChild(o);
			// 	// o.style.width = '100vw';
			// 	// o.style.height = '100vh';

			// }

			// // allows proper download
			// const link = document.createElement("a");
			// link.href = pdfUrl;
			// link.download = "myFile.pdf"; // Set the desired filename here
			// document.body.appendChild(link);
			// link.click();

			//document.location.href = pdfUrl;

		});

	}, []);

	return (
		<>
			{
				(!pdfUrl.current && !title.startsWith("Error")) &&
				<div className="flex vert height-100vh justify-center gap-5">
					<Icon type="logo-shield" size="large" style={{width:"32px", height:"32px"}} />
					Loading, please wait...
					<Spinner size="small" />
				</div>
			}

			{
				(pdfUrl.current || title.startsWith("Error")) &&
				<div className="flex vert bg-black">
					<div
						className="text-inverse flex horiz align-center gap-5"
						style={{
							height:"50px"
						}}
					>
						<Icon type="logo-shield" size="large" style={{width:"32px", height:"32px"}} />
						<b className="font-med">{title}</b>
						<a
							className="button primary"
							href={pdfUrl.current}
							download={title}
						>Download</a>							
					</div>

					<embed src={pdfUrl.current} 
						style={{
							width: "100vw",
							height: "calc(100vh - 50px)",
						}} 
					/>
					
				</div>
			}
		</>
	)
}


export function GenerateWorkflowZip(props: {
	awfID: string,
}) {

	const count = useRef({
		total: 0,
		current: 0,
		desc: "" as any,
		complete: false,
	});

	const refresh = useRefresh();

	useEffect(() => {
		api.account_wf_details({ awf: props.awfID }).then((resp) => {
			createZip(resp).then(() => {

			}).catch(() => {
	
			});
	

		}).catch((e) => {
	
		});	
	}, []);

	async function createZip(awf: account_wf) {

		const zip = new JSZip();

		const forms = (awf.resources && awf.resources.forms) ? awf.resources.forms : [];
		
		count.current.total = 0;
		count.current.current = 1;

		for(let f of forms) {
			count.current.total = (awf.resources.urls ? awf.resources.urls.length : 0);

			if (awf.resources.docs) {
				for(let d of awf.resources.docs) {
					if (d.type === "event") {
						count.current.total++;
					} else if (d.blob) {
						count.current.total++;
					}
				}
			}
		}

		refresh.refresh();

		for(let f of forms) {

			if (awf.resources.docs) {
				for(let d of awf.resources.docs) {
					if (d.type === "event") {
						const pdf = await createEventPdf(d.event!);					
						zip.file(sanitiseFilename(d.title) + ".pdf", pdf.bytes);							
					
					} else if (d.blob) {
						const resp = await fetch(api.getDownloadUrl(d.blob));
						const data = await resp.blob();
						zip.file(sanitiseFilename(d.title) + d.ext, data);

					}
				}
			}

			if (awf.resources.urls) {
				for(let u of awf.resources.urls) {
					const title = u.title.replace("{title}", awf.title);
					const urlParts = u.url.split("/");
					const defID = urlParts[2];

					const pdf = await createReportPdf(defID, f);
					zip.file(sanitiseFilename(title) + ".pdf", pdf!.bytes);

					count.current.current++;
					refresh.refresh();
				}
			}

		}
		
		count.current.desc = "Compressing";
		refresh.refresh();

		const zipBlob = await zip.generateAsync({ type: "blob" });

		const filename = sanitiseFilename(awf.title + ".zip");
		count.current.desc = <span className="flex horiz gap-2 text-inverse"><Icon type="tick" size="small" />Complete</span>;
		count.current.complete = true;
		refresh.refresh();

		const PREVENT_DOWNLOAD = false;

		if (!PREVENT_DOWNLOAD) {
			const url = URL.createObjectURL(zipBlob);
			const a = document.createElement("a");
			a.href = url;
			a.download = sanitiseFilename(filename);
			a.click();
			URL.revokeObjectURL(url);

			setTimeout(() => {
				window.close();
			}, 1000);
		}

	}

	function sanitiseFilename(fn: string) {
		return fn.replace(/[^A-Za-z0-9\-_.!@ ]/g, '_');
	}


	return (
		<div className="flex vert width-100vw height-100vh fixed justify-center">
			{
				!count.current.complete &&
				<Spinner size="small" />
			}

			<div className="flex vert gap-5">
				{
					!count.current.complete &&
					<div>Preparing</div>
				}
				{
					(count.current.current > 0 && count.current.current <= count.current.total) &&
					<div className="count primary">{`${count.current.current} of ${count.current.total}`}</div>
				}
				{
					(count.current.desc) &&
					<div className="count success">{count.current.desc}</div>
				}
			</div>
		</div>
	);
}
